import { supabase } from '../lib/supabase'

export interface WaitlistSignup {
  id: string
  email: string
  name?: string
  created_at: string
  updated_at: string
  metadata: any
  source: string
  is_notified: boolean
}

export interface LaunchSettings {
  id: string
  launch_date: string
  is_active: boolean
  created_at: string
  updated_at: string
  created_by?: string
  notes?: string
}

class WaitlistService {
  async joinWaitlist(email: string, name?: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { error } = await supabase
        .from('waitlist_signups')
        .insert({
          email: email.toLowerCase().trim(),
          name: name?.trim(),
          source: 'countdown_page',
          metadata: {
            user_agent: navigator.userAgent,
            referrer: document.referrer,
            timestamp: new Date().toISOString()
          }
        })

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          return {
            success: false,
            error: 'This email is already on the waitlist!'
          }
        }
        throw error
      }

      return { success: true }
    } catch (error) {
      console.error('Error joining waitlist:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to join waitlist. Please try again.'
      }
    }
  }

  async getLaunchSettings(): Promise<LaunchSettings | null> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('launch_settings')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (error) {
        if (error.code === 'PGRST116') { // No rows found
          return null
        }
        throw error
      }

      return data
    } catch (error) {
      console.error('Error getting launch settings:', error)
      return null
    }
  }

  async getWaitlistCount(): Promise<number> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { count, error } = await supabase
        .from('waitlist_signups')
        .select('*', { count: 'exact', head: true })

      if (error) throw error
      return count || 0
    } catch (error) {
      console.error('Error getting waitlist count:', error)
      return 0
    }
  }

  // Admin functions
  async getAllWaitlistSignups(): Promise<WaitlistSignup[]> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('waitlist_signups')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error getting waitlist signups:', error)
      return []
    }
  }

  async updateLaunchDate(newDate: string, notes?: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      // Deactivate old settings
      await supabase
        .from('launch_settings')
        .update({ is_active: false })
        .eq('is_active', true)

      // Insert new setting
      const { error } = await supabase
        .from('launch_settings')
        .insert({
          launch_date: newDate,
          notes,
          is_active: true
        })

      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error updating launch date:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update launch date'
      }
    }
  }
}

export const waitlistService = new WaitlistService() 